import { Profile } from './types';
import { AI_CONFIG } from './ai/config';

const XAI_API_URL = 'https://api.x.ai/v1/chat/completions';

async function callXAI(prompt: string, options?: {
  temperature?: number;
  maxTokens?: number;
}): Promise<{ text: string; suggestions?: string[] }> {
  try {
    const body = {
      messages: [
        {
          role: 'system',
          // content: 'You are a helpful AI assistant for research and academic purposes.'
          content: 'You are a helpful research assistant for high school students.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      model: 'grok-2-latest',
      // stream: false,
      temperature: options?.temperature ?? 0.7,
      max_tokens: options?.maxTokens ?? 1000
    };
    const response = await fetch(XAI_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${AI_CONFIG.XAI_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    
    try {
      // Parse the JSON response
      const jsonMatch = data.choices[0].message.content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          text: parsed.message,
          suggestions: parsed.suggestions
        };
      }
      
      // Fallback to using the raw response
      return {
        text: data.choices[0].message.content.trim(),
        suggestions: []
      };
    } catch (error) {
      console.error('Failed to parse AI response:', error);
      return {
        text: data.choices[0].message.content.trim(),
        suggestions: []
      };
    }

    // if (!response.ok) {
    //   const errorData = await response.json().catch(() => null);
    //   throw new Error(
    //     errorData?.error?.message || 
    //     `xAI API error: ${response.status} ${response.statusText}`
    //   );
    // }

    // const data = await response.json();
    // return {
    //   text: data.choices[0].message.content,
    //   suggestions: data.choices[0].message.suggestions
    // };
  } catch (error) {
    console.error('xAI API error:', error);
    throw error;
  }
}

// Email Generation
export async function generateEmail(params: {
  professorName: string;
  professorResearchInterests: string[];
  professorDepartment: string;
  professorUniversity: string;
  studentProfile: Partial<Profile>;
}): Promise<{ subject: string; body: string; error?: string }> {
  const prompt = `Generate a professional email with subject line for a research opportunity inquiry with this context:

Professor Details:
- Name: ${params.professorName}
- Department: ${params.professorDepartment}
- University: ${params.professorUniversity}
- Research Interests: ${params.professorResearchInterests.join(', ')}

Student Details:
${params.studentProfile.full_name ? `Name: ${params.studentProfile.full_name}` : ''}
${params.studentProfile.school ? `School: ${params.studentProfile.school}` : ''}
${params.studentProfile.grade_level ? `Grade Level: ${params.studentProfile.grade_level}` : ''}
${params.studentProfile.research_interests?.length ? `Research Interests: ${params.studentProfile.research_interests.join(', ')}` : ''}
${params.studentProfile.achievements?.length ? `Achievements: ${params.studentProfile.achievements.join(', ')}` : ''}
${params.studentProfile.skills?.length ? `Skills: ${params.studentProfile.skills.join(', ')}` : ''}

Generate a professional email with a subject line. Format the response as JSON with 'subject' and 'body' fields.`;
  try {
    const response = await callXAI(prompt);
    const text = response.text.slice(response.text.indexOf("{"), response.text.lastIndexOf("}")+1);
    const emailData = JSON.parse(text);
    return emailData;
  } catch (error) {
    return {
      subject: `Research Opportunity Inquiry - ${params.professorResearchInterests[0]}`,
      body: `Dear Professor ${params.professorName},

I hope this email finds you well. My name is ${params.studentProfile.full_name !== undefined || '[Your Name]'}, and I am a ${params.studentProfile.grade_level !== undefined || 'high school'} student at ${params.studentProfile.school !== undefined || '[Your School]'}. I am deeply interested in ${params.professorResearchInterests[0]} and have been following your work in the ${params.professorDepartment} department at ${params.professorUniversity}.

${params.studentProfile.research_interests?.length ? `My research interests include ${params.studentProfile.research_interests.join(', ')}, and ` : ''}I am writing to inquire about the possibility of getting involved in research work under your guidance.

${params.studentProfile.achievements?.length ? `Some of my relevant achievements include:\n- ${params.studentProfile.achievements.join('\n- ')}\n\n` : ''}${params.studentProfile.skills?.length ? `I have experience with ${params.studentProfile.skills.join(', ')}.\n\n` : ''}I would be grateful for the opportunity to discuss potential research opportunities in your lab.

Thank you for considering my request.

Best regards,
${params.studentProfile.full_name !== undefined || '[Your Name]'}`
    };
  }
}

// Chat Interface
export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export async function sendChatMessage(messages: Message[]): Promise<{
  message: string;
  error?: string;
}> {
  try {
    const prompt = messages
      .map(m => `${m.role}: ${m.content}`)
      .join('\n');

    const response = await callXAI(prompt);
    return { message: response.text };
  } catch (error) {
    return {
      message: 'I apologize, but I encountered an error. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// Research Assistant
interface ResearchAssistantResponse {
  message: string;
  suggestions?: string[];
  error?: string;
}

export async function getResearchAssistantResponse(
  query: string,
  context?: {
    professorName?: string;
    researchArea?: string;
    studentProfile?: any;
  }
): Promise<ResearchAssistantResponse> {
  try {
    const isResearchRelated = /research|study|experiment|hypothesis|data|analysis|scientific|publication/i.test(query);
    
    if (!isResearchRelated) {
      return {
        message: "I am only a research assistant and will answer questions only related to research topics."
      };
    }
    const prompt = `You are an AI research assistant helping a high school student with their research journey.
Context: ${context ? `
Professor: ${context.professorName || 'Not specified'}
Research Area: ${context.researchArea || 'Not specified'}
Student Profile: ${JSON.stringify(context.studentProfile || {})}` : 'No specific context provided'}

Student Query: ${query}

Please provide a helpful response that:
1. Directly addresses the student's question
2. Offers specific, actionable advice
3. Includes relevant suggestions or next steps
4. Maintains a supportive and encouraging tone`;

    const response = await callXAI(prompt);
    return {
      message: response.text,
      suggestions: response.suggestions ?? [],
      error: ""
    };
  } catch (error) {
    return {
      message: 'I apologize, but I encountered an error processing your request. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      suggestions: []
    };
  }
}

// Professor Profile Enhancement
export async function generateProfessorProfile(baseProfile: any): Promise<any> {
  const prompt = `Generate a detailed academic profile for a professor with these characteristics:
Name: ${baseProfile.name}
Department: ${baseProfile.department}
University: ${baseProfile.university}
Research Interests: ${baseProfile.researchInterests?.join(', ')}

Please provide a detailed academic profile in JSON format with:
1. A list of 3-5 current research projects with titles and brief descriptions
2. A list of 3-5 potential collaboration areas
3. A list of 2-3 specialized research topics
4. Primary teaching areas
5. Notable achievements or specializations`;

  try {
    const response = await callXAI(prompt);
    const generatedProfile = JSON.parse(response.text);

    return {
      ...baseProfile,
      researchInterests: [
        ...(baseProfile.researchInterests || []),
        ...generatedProfile.specializedTopics
      ],
      publications: [
        ...(baseProfile.publications || []),
        ...generatedProfile.currentProjects.map((project: any, index: number) => ({
          id: `generated-${index}`,
          title: project.title,
          year: new Date().getFullYear() - Math.floor(Math.random() * 3),
          journal: 'Research in Progress',
          citations: Math.floor(Math.random() * 50),
          url: baseProfile.website || '',
          abstract: project.description
        }))
      ],
      department: baseProfile.department || generatedProfile.teachingAreas[0],
      verified: true
    };
  } catch (error) {
    console.warn('Failed to enhance professor profile:', error);
    return baseProfile;
  }
}